import React, { useEffect, useRef, useState } from 'react'
import { Input, Modal } from 'antd'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import locationImg from '@/assets/images/location.svg'
import { getRegionCodesByNames } from './shop-address'

export const BAIDU_MAP_KEY = 'I1l6aGmZIgkUWerovjVSfTNkIbHGWNm9'
let mapLoadPromise: Promise<any> | null = null

const loadMap = (): Promise<any> => {
    const globalWindow = window as any
    if (globalWindow.BMap) return Promise.resolve(globalWindow.BMap)
    if (!mapLoadPromise) {
        mapLoadPromise = new Promise((resolve, reject) => {
            const callbackName = `senguoBaiduMapReady_${Date.now()}`
            const script = document.createElement('script')
            globalWindow[callbackName] = () => {
                delete globalWindow[callbackName]
                resolve(globalWindow.BMap)
            }
            script.src = `https://api.map.baidu.com/api?v=2.0&ak=${BAIDU_MAP_KEY}&callback=${callbackName}`
            script.onerror = () => {
                delete globalWindow[callbackName]
                script.remove()
                mapLoadPromise = null
                reject(new Error('地图加载失败'))
            }
            document.head.appendChild(script)
        })
    }

    return mapLoadPromise
}

export interface MapPoint {
    longitude: number
    latitude: number
}

export interface MapSelection extends MapPoint {
    regionCodes: string[]
    detailedAddress: string
}

interface Suggestion {
    title: string
    address: string
    point: { lng: number; lat: number }
}

interface Props {
    visible: boolean
    address: string
    city: string
    point?: MapPoint | null
    onCancel: () => void
    onSelect: (selection: MapSelection) => void
}

const MapPicker: React.FC<Props> = ({ visible, address, city, point, onCancel, onSelect }) => {
    const mapElement = useRef<HTMLDivElement>(null)
    const mapApi = useRef<any>()
    const mapInstance = useRef<any>()
    const selectPoint = useRef<(location: any, preferredAddress?: string) => void>()
    const lookupId = useRef(0)
    const searchId = useRef(0)
    const debounceTimer = useRef<number | undefined>()
    const composing = useRef(false)
    const [search, setSearch] = useState(address)
    const [selected, setSelected] = useState<MapSelection | null>(null)
    const [locationText, setLocationText] = useState('')
    const [loading, setLoading] = useState(false)
    const [locating, setLocating] = useState(false)
    const [suggestions, setSuggestions] = useState<Suggestion[]>([])
    const [searching, setSearching] = useState(false)

    useEffect(() => {
        if (!visible) return
        let active = true
        setSearch(address)
        setSelected(null)
        setLocationText('')
        setSuggestions([])
        setSearching(false)
        setLoading(true)
        loadMap()
            .then(BMap => {
                if (!active || !mapElement.current) return
                mapApi.current = BMap
                const map = new BMap.Map(mapElement.current)
                mapInstance.current = map
                const center = new BMap.Point(point?.longitude || 116.404, point?.latitude || 39.915)
                map.centerAndZoom(center, point ? 16 : 11)
                map.enableScrollWheelZoom(true)

                const selectLocation = (location: any, preferredAddress?: string) => {
                    const requestId = ++lookupId.current
                    map.clearOverlays()
                    map.addOverlay(new BMap.Marker(location))
                    map.panTo(location)
                    setSelected(null)
                    setLocating(true)
                    new BMap.Geocoder().getLocation(location, (result: any) => {
                        if (!active || requestId !== lookupId.current) return
                        setLocating(false)
                        const components = result?.addressComponents
                        const regionCodes = components
                            ? getRegionCodesByNames(components.province, components.city, components.district)
                            : []
                        const stripRegion = (text: string) =>
                            text
                                .replace(components?.province || '', '')
                                .replace(components?.city || '', '')
                                .replace(components?.district || '', '')
                        const streetAddress = `${components?.street || ''}${components?.streetNumber || ''}`
                        const fullAddress = result?.address || ''
                        // 候选项命中时优先采用用户看到并选择的地址，避免 POI 坐标反解析偏差
                        const detailedAddress =
                            (preferredAddress ? stripRegion(preferredAddress) : '') ||
                            streetAddress ||
                            stripRegion(fullAddress) ||
                            result?.surroundingPois?.[0]?.title ||
                            ''
                        setLocationText(preferredAddress || fullAddress || '未能识别地址')
                        if (regionCodes.length === 3 && detailedAddress) {
                            setSelected({
                                longitude: location.lng,
                                latitude: location.lat,
                                regionCodes,
                                detailedAddress: detailedAddress.slice(0, 64),
                            })
                        }
                    })
                }
                selectPoint.current = selectLocation

                map.addEventListener('click', (event: any) => selectLocation(event.point))
                if (point) {
                    selectLocation(center)
                } else if (address || city) {
                    const requestId = ++searchId.current
                    new BMap.Geocoder().getPoint(
                        address || city,
                        (found: any) => {
                            if (!active || requestId !== searchId.current || !found) return
                            map.centerAndZoom(found, 16)
                            selectLocation(found)
                        },
                        city,
                    )
                }
            })
            .catch(() => active && $msg('error', '地图加载失败，请检查网络后重试'))
            .finally(() => active && setLoading(false))

        return () => {
            active = false
            if (debounceTimer.current) window.clearTimeout(debounceTimer.current)
            lookupId.current++
            searchId.current++
            mapInstance.current = null
            selectPoint.current = undefined
        }
    }, [visible, address, city, point?.longitude, point?.latitude])

    const locate = () => {
        if (!mapApi.current || !mapInstance.current || !search.trim()) return
        const requestId = ++searchId.current
        new mapApi.current.Geocoder().getPoint(
            search.trim(),
            (found: any) => {
                if (requestId !== searchId.current || !mapInstance.current) return
                if (!found) {
                    $msg('warning', '没有找到对应位置，请尝试更完整的地址')

                    return
                }
                mapInstance.current.centerAndZoom(found, 16)
                selectPoint.current?.(found)
            },
            city,
        )
    }

    const searchPlaces = (keyword?: string) => {
        const text = (keyword ?? search).trim()
        const requestId = ++searchId.current
        if (!text) {
            setSearching(false)
            setSuggestions([])
            return
        }
        if (!mapApi.current || !mapInstance.current) return
        setSearching(true)
        setSuggestions([])
        const local = new mapApi.current.LocalSearch(mapInstance.current, {
            pageCapacity: 10,
            renderOptions: { autoViewport: false, selectFirstResult: false },
            onSearchComplete: (results: any) => {
                if (requestId !== searchId.current) return
                setSearching(false)
                const count = results?.getCurrentNumPois?.() || 0
                if (!count) {
                    locate()

                    return
                }
                const items: Suggestion[] = []
                for (let i = 0; i < count; i++) {
                    const poi = results.getPoi(i)
                    if (!poi || !poi.point) continue
                    items.push({
                        title: poi.title || '',
                        address: poi.address || '',
                        point: { lng: poi.point.lng, lat: poi.point.lat },
                    })
                }
                setSuggestions(items)
            },
        })
        local.search(text)
    }

    const debouncedSearch = (keyword: string) => {
        if (debounceTimer.current) window.clearTimeout(debounceTimer.current)
        debounceTimer.current = window.setTimeout(() => searchPlaces(keyword), 300)
    }

    const chooseSuggestion = (suggestion: Suggestion) => {
        setSuggestions([])
        setSearch(suggestion.title)
        if (!mapApi.current || !mapInstance.current || !selectPoint.current) return
        const point = new mapApi.current.Point(suggestion.point.lng, suggestion.point.lat)
        mapInstance.current.centerAndZoom(point, 17)
        selectPoint.current(point, suggestion.address)
    }

    return (
        <Modal
            title={
                <div className="setting-info__map-modal-title">
                    <span>地图选地址</span>
                    <span className="setting-info__map-modal-subtitle">搜索或在地图中点击选取店铺精准位置</span>
                </div>
            }
            visible={visible}
            centered
            width={700}
            className="setting-info__map-modal"
            onCancel={onCancel}
            onOk={() => selected && onSelect(selected)}
            okButtonProps={{ disabled: !selected || locating }}
            okText="使用此地址"
            cancelText="取消"
            destroyOnClose
        >
            <div className="setting-info__map-search-wrap">
                <div className="setting-info__map-search">
                    <Input
                        value={search}
                        onChange={event => {
                            const value = event.target.value
                            setSearch(value)
                            if (!composing.current) debouncedSearch(value)
                        }}
                        onCompositionStart={() => {
                            composing.current = true
                        }}
                        onCompositionEnd={event => {
                            composing.current = false
                            const value = (event.target as HTMLInputElement).value
                            setSearch(value)
                            debouncedSearch(value)
                        }}
                        onPressEnter={() => searchPlaces()}
                        placeholder="输入要搜索的地点（如小区、大厦、路名）"
                        aria-label="搜索地图地址"
                        allowClear
                        prefix={<i className="iconfont icon6sousuo999-20 setting-info__map-search-icon" />}
                    />
                    <Button
                        types="primary"
                        onClick={() => searchPlaces()}
                        loading={searching}
                        disabled={loading || searching}
                    >
                        {searching ? '搜索中' : '搜索'}
                    </Button>
                </div>
                {suggestions.length > 0 && (
                    <div className="setting-info__map-suggestions-wrapper">
                        <div className="setting-info__map-suggestions-header">
                            <span>候选地点（共 {suggestions.length} 条，点击直接定位）</span>
                        </div>
                        <ul className="setting-info__map-suggestions" aria-label="搜索结果">
                            {suggestions.map((suggestion, index) => (
                                <li
                                    key={`${suggestion.title}-${suggestion.point.lng}-${suggestion.point.lat}-${index}`}
                                >
                                    <button
                                        type="button"
                                        className="setting-info__map-suggestion-btn"
                                        onClick={() => chooseSuggestion(suggestion)}
                                    >
                                        <div className="setting-info__map-suggestion-icon-wrap">
                                            <img
                                                src={locationImg}
                                                alt=""
                                                className="setting-info__map-suggestion-icon"
                                            />
                                        </div>
                                        <div className="setting-info__map-suggestion-content">
                                            <strong className="setting-info__map-suggestion-title">
                                                {suggestion.title}
                                            </strong>
                                            <span className="setting-info__map-suggestion-address">
                                                {suggestion.address || '暂无详细街道信息'}
                                            </span>
                                        </div>
                                        <span className="setting-info__map-suggestion-tag">选取</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            <div ref={mapElement} className="setting-info__map" role="img" aria-label="点击地图选择店铺地址" />
            <div
                className={`store-setting-page__hint setting-info__map-status ${
                    loading || locating
                        ? 'setting-info__map-status--loading'
                        : selected
                        ? 'setting-info__map-status--success'
                        : locationText
                        ? 'setting-info__map-status--warning'
                        : 'setting-info__map-status--info'
                }`}
            >
                {loading && (
                    <div className="setting-info__map-status-content">
                        <span className="setting-info__map-status-dot setting-info__map-status-dot--pulse" />
                        <span>地图资源加载中，请稍候…</span>
                    </div>
                )}
                {!loading && locating && (
                    <div className="setting-info__map-status-content">
                        <span className="setting-info__map-status-dot setting-info__map-status-dot--pulse" />
                        <span>正在识别选点地址及省市区信息…</span>
                    </div>
                )}
                {!loading && !locating && selected && (
                    <div className="setting-info__map-status-content">
                        <img src={locationImg} alt="" className="setting-info__map-status-icon" />
                        <span className="setting-info__map-status-label">已选位置：</span>
                        <span className="setting-info__map-status-address" title={locationText}>
                            {locationText}
                        </span>
                        <span className="setting-info__map-status-badge">匹配成功</span>
                    </div>
                )}
                {!loading && !locating && !selected && locationText && (
                    <div className="setting-info__map-status-content">
                        <span className="setting-info__map-status-warn-icon">!</span>
                        <span className="setting-info__map-status-address" title={locationText}>
                            {locationText}
                        </span>
                        <span className="setting-info__map-status-warn-text">
                            未能匹配完整省市区及详细地址，请点击有效区域重新选点
                        </span>
                    </div>
                )}
                {!loading && !locating && !selected && !locationText && (
                    <div className="setting-info__map-status-content">
                        <span className="setting-info__map-status-dot" />
                        <span>在上方输入地点搜索，或在地图中点击选择店铺位置</span>
                    </div>
                )}
            </div>
        </Modal>
    )
}

export default MapPicker
